import { js2xml, xml2js } from 'xml-js';

// 转义XML特殊字符
const escapeXml = (str) => {
    if (typeof str !== 'string') {
        return String(str);
    }
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
};

// 验证XML标签名是否有效
const isValidXmlName = (name) => {
    if (!name || name.length === 0) return false;
    // XML名称必须以字母、下划线或冒号开头，可以包含字母、数字、下划线、连字符、点、冒号
    return /^[a-zA-Z_:][a-zA-Z0-9._:-]*$/.test(name);
};

// 将键名转换为有效的XML标签名
const sanitizeXmlName = (name) => {
    if (isValidXmlName(name)) {
        return name;
    }
    // 如果无效，替换为安全的名称
    return name.replace(/[^a-zA-Z0-9._:-]/g, '_');
};

// 手动构建XML字符串，确保数组正确处理
const buildXml = (obj, indent = 0, tagName = 'item', isArrayElement = false) => {
    const spaces = '  '.repeat(indent);
    
    if (obj === null) {
        return `${spaces}<${sanitizeXmlName(tagName)}></${sanitizeXmlName(tagName)}>`;
    }
    
    if (typeof obj !== 'object') {
        return `${spaces}<${sanitizeXmlName(tagName)}>${escapeXml(String(obj))}</${sanitizeXmlName(tagName)}>`;
    }
    
    if (Array.isArray(obj)) {
        if (obj.length === 0) {
            return `${spaces}<${sanitizeXmlName(tagName)}></${sanitizeXmlName(tagName)}>`;
        }
        
        // 如果是数组元素（已经在数组容器内），直接使用 item 标签
        if (isArrayElement) {
            let result = '';
            for (const item of obj) {
                result += buildXml(item, indent, 'item', true) + '\n';
            }
            return result.slice(0, -1); // 移除最后的换行符
        } else {
            // 如果是对象属性中的数组，使用父键名作为容器
            let result = `${spaces}<${sanitizeXmlName(tagName)}>\n`;
            for (const item of obj) {
                result += buildXml(item, indent + 1, 'item', true) + '\n';
            }
            result += `${spaces}</${sanitizeXmlName(tagName)}>`;
            return result;
        }
    }
    
    // 处理对象
    let result = `${spaces}<${sanitizeXmlName(tagName)}>\n`;
    for (const key in obj) {
        const value = obj[key];
        if (Array.isArray(value)) {
            // 数组：使用父键名作为容器，内部使用 item 标签
            result += `${spaces}  <${sanitizeXmlName(key)}>\n`;
            for (const item of value) {
                result += buildXml(item, indent + 2, 'item', true) + '\n';
            }
            result += `${spaces}  </${sanitizeXmlName(key)}>\n`;
        } else {
            result += buildXml(value, indent + 1, key, false) + '\n';
        }
    }
    result += `${spaces}</${sanitizeXmlName(tagName)}>`;
    return result;
};

export const jsonToXml = (jsonStr) => {
    try {
        const obj = JSON.parse(jsonStr);
        const xmlContent = buildXml(obj, 1, 'root');
        return `<?xml version="1.0" encoding="UTF-8"?>\n${xmlContent}`;
    } catch (e) {
        throw new Error("Invalid JSON input");
    }
};

// 将xml-js的compact格式转换为JSON对象
const convertToJson = (obj) => {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(convertToJson);
    }

    const keys = Object.keys(obj);
    
    // 如果是文本节点（只有 _text 属性）
    if (keys.length === 1 && keys[0] === '_text') {
        const text = String(obj._text);
        // 尝试转换为数字或布尔值
        if (text === 'true') return true;
        if (text === 'false') return false;
        if (text === 'null' || text === '') return null;
        if (/^-?\d+$/.test(text)) return parseInt(text, 10);
        if (/^-?\d*\.\d+$/.test(text)) return parseFloat(text);
        return text;
    }

    // 检查是否是数组结构（包含 item 元素）
    if (keys.length === 1 && keys[0] === 'item') {
        const items = Array.isArray(obj.item) ? obj.item : [obj.item];
        return items.map(convertToJson);
    }

    // 处理对象
    const result = {};
    for (const key in obj) {
        const value = obj[key];
        
        if (value === null || value === undefined) {
            result[key] = null;
        } else if (Array.isArray(value)) {
            // 值本身是数组（xml-js可能返回这种情况）
            result[key] = value.map(convertToJson);
        } else if (typeof value === 'object') {
            // 检查是否是数组容器（包含 item 属性）
            if ('item' in value) {
                // 这是一个数组容器
                const items = Array.isArray(value.item) ? value.item : [value.item];
                result[key] = items.map(convertToJson);
            } else if ('_text' in value && Object.keys(value).length === 1) {
                // 这是文本节点
                result[key] = convertToJson(value);
            } else {
                // 这是普通对象
                result[key] = convertToJson(value);
            }
        } else {
            result[key] = convertToJson(value);
        }
    }
    
    return result;
};

export const xmlToJson = (xmlStr) => {
    try {
        const options = {
            compact: true,
            ignoreComment: true,
            spaces: 4,
            nativeType: false // 我们自己处理类型转换
        };
        const result = xml2js(xmlStr, options);

        let cleanedResult = result;

        // Unwrap 'root' wrapper
        if (result.root) {
            cleanedResult = result.root;
        }

        // 转换为JSON对象
        const finalResult = convertToJson(cleanedResult);

        return JSON.stringify(finalResult, null, 4);
    } catch (e) {
        throw new Error("Invalid XML input: " + e.message);
    }
};
